import { PageHeader } from '@/components/shared/PageHeader';
import { Loader2 } from 'lucide-react';
import { ExportOperationsButton } from './ExportOperationsButton';
import { fmtEur, lineLabel, PeriodFilter, useOperationsPeriod } from './format';
import { useOperationsSummary } from './hooks/useOperations';

export function OperationsResultPage() {
    const { from, to, setPeriod } = useOperationsPeriod();
    const { data, isLoading, error } = useOperationsSummary(from, to);

    const totals = (data ?? []).reduce(
        (acc, row) => ({
            income: acc.income + row.incomeBaseCents,
            vat: acc.vat + row.vatCents,
            suplido: acc.suplido + row.suplidoCents,
            collected: acc.collected + row.collectedCents,
            expense: acc.expense + row.expenseBaseCents,
            expenseVat: acc.expenseVat + row.expenseVatCents,
            margin: acc.margin + row.marginCents,
        }),
        { income: 0, vat: 0, suplido: 0, collected: 0, expense: 0, expenseVat: 0, margin: 0 }
    );

    return (
        <div className="space-y-6">
            <PageHeader
                title="Resultado"
                description="Ingreso es la base imponible de lo que es de AIPECES. El margen resta la base de los gastos de la línea. El IVA y el suplido quedan aparte."
            />
            <div className="flex flex-wrap items-end gap-3">
                <PeriodFilter from={from} to={to} onChange={setPeriod} />
                <ExportOperationsButton from={from} to={to} />
            </div>

            {error && (
                <p className="text-sm text-destructive">
                    {(error as Error).message}. Hace falta desplegar la API de operaciones para ver estos datos.
                </p>
            )}

            <div className="rounded-xl border border-border overflow-x-auto">
                <table className="w-full text-sm min-w-[880px]">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Línea</th>
                            <th className="px-4 py-3 text-right">Ingreso</th>
                            <th className="px-4 py-3 text-right">IVA repercutido</th>
                            <th className="px-4 py-3 text-right">Suplido</th>
                            <th className="px-4 py-3 text-right">Cobrado</th>
                            <th className="px-4 py-3 text-right">Gasto</th>
                            <th className="px-4 py-3 text-right">IVA soportado</th>
                            <th className="px-4 py-3 text-right">Margen</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {isLoading && (
                            <tr>
                                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                                    Cargando…
                                </td>
                            </tr>
                        )}
                        {data?.map((row) => (
                            <tr key={row.businessLine}>
                                <td className="px-4 py-3 font-medium">{lineLabel(row.businessLine)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(row.incomeBaseCents)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(row.vatCents)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(row.suplidoCents)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(row.collectedCents)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(row.expenseBaseCents)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(row.expenseVatCents)}</td>
                                <td className="px-4 py-3 text-right font-semibold">{fmtEur(row.marginCents)}</td>
                            </tr>
                        ))}
                        {data && data.length > 0 && (
                            <tr className="bg-muted/20 font-semibold">
                                <td className="px-4 py-3">Total</td>
                                <td className="px-4 py-3 text-right">{fmtEur(totals.income)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(totals.vat)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(totals.suplido)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(totals.collected)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(totals.expense)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(totals.expenseVat)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(totals.margin)}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-muted-foreground max-w-3xl">
                El suplido es la tasa de licencia que pasa a la administración: no es ingreso ni gasto.
                Las rectificativas restan. Notaría, registro y gestoría no entran en esta pantalla.
                El gasto incluye la comisión que Stripe descuenta en cada cobro, con la fecha del cargo y sin IVA. Un reembolso no la devuelve.
            </p>
        </div>
    );
}
